import type { ReceiptTypeName } from '../transaction-phoenix/api';
import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  defaultHeaders,
  get,
  getPhoenixApiTenantUrl,
  modify,
  superFetch,
} from '@pflegenavi/shared-frontend/platform';
import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';
import { Filter } from '../filter';
import { addMissingFactorsAndSort } from '../cash-management';

export interface LineItemTax {
  id: string;
  tax_amount: string;
  country_id?: string;
  tax_name: string;
  explanation_short?: string;
  explanation_long?: string;
  display_explanation?: boolean;
  paragraph?: string;
  tax_abbreviation?: string;
  country?: {
    id: string;
    iso_code: string;
    iso_code_3: string;
    numeric_code: string;
    name: string;
  };
}

export interface LineItem {
  id: string;
  amount_per_item: number;
  count: number;
  name?: string;
  line_item_sequence_nr?: number;
  taxes: LineItemTax[];
}

export interface ReceiptBatchEntry {
  id: string;
  resident: {
    id: string;
    firstname: string;
    lastname: string;
    gender: string;
    state: string;
  };
  receipt_image_ids: string[];
  receipt_date?: Date;
  amount: number;
  additional_fee_amount: number;
  included_fee_amount: number;
  notes: string;
  line_items: LineItem[];
}

export interface ReceiptBatch {
  id: string;
  created_on: Date;
  updated_on: Date;
  receipt_date?: Date;
  receipt_image_ids?: string[];
  receipt_type: {
    id: string;
    name: ReceiptTypeName;
    requires_service_provider: boolean;
  };
  service_provider?: {
    id: string;
    name: string;
  };
  cash_list_transaction_group_id?: string | null;
  receipt_batch_entries: ReceiptBatchEntry[];
  nursing_home_id: string;
  owner: 'nursing_home' | 'service_provider';
  submission_locked: boolean;
  use_individual_dates: boolean;
  entry_count: number;
  invalid_entry_count: number;
  total_amount: number;
  total_additional_fee_amount: number;
  total_included_fee_amount: number;
  min_receipt_date?: Date;
  max_receipt_date?: Date;
  generate_receipts: boolean;
  earnings: boolean;
  submission_status: 'pending' | 'successful' | 'failed' | null;
  submission_oban_job_id: number | null;
  job: {
    id: string;
    status: 'pending' | 'partial' | 'successful' | 'failed';
    submission_date: Date;
  } | null;
  recurring_item_receipt_batch: {
    id: string;
    name: string;
    amount: number;
    archived: boolean;
    auto_add_residents: boolean;
    charge_full_period: boolean;
    compute_daily: boolean;
    tax_code?: {
      id: string;
      tax_name: string;
      tax_abbreviation?: string;
      tax_amount: string;
      explanation_short?: string;
      explanation_long?: string;
      display_explanation: boolean;
      paragraph?: string;
    };
    generate_receipts: boolean;
  } | null;
  payment: {
    amount: number;
    currency: string;
    service_provider_payment: {
      id: string;
      internal_status: 'Completed' | 'Failed' | 'Disputed' | 'Pending';
    };
  } | null;
  cash_transaction_group_list?: {
    id: string;
    cash_list_id: string;
    update_date: Date;
    semantic_creation_date: Date;
    /** Employee of the group's earliest Pliant transaction, else the group's creator. */
    semantic_created_by: string | null;
    cash_transaction_notes: string[];
    cash_amount: number;
    separate_fee_amount: number;
    immediate_fee_amount: number;
    link_count: number;
    linked_amount: number;
    linked_separate_fee_amount: number;
    linked_immediate_fee_amount: number;
  };
}

export interface UpdateLineItemTax {
  id?: string;
  tax_amount: string;
  country_id?: string;
  tax_name: string;
  explanation_short?: string;
  explanation_long?: string;
  display_explanation?: boolean;
  paragraph?: string;
  tax_abbreviation?: string;
}

export interface UpdateReceiptBatchEntry {
  id?: string;
  resident_id?: string | undefined;
  receipt_image_ids?: string[];
  receipt_date?: Date;
  amount?: number;
  additional_fee_amount?: number;
  included_fee_amount?: number;
  notes?: string;
  line_items?: Array<{
    id: string | undefined;
    amount_per_item: number;
    name: string | undefined;
    taxes?: UpdateLineItemTax[];
  }>;
}
export interface UpdateReceiptBatch {
  id: string;
  receipt_date?: Date;
  receipt_image_ids?: string[];
  receipt_batch_entries?: UpdateReceiptBatchEntry[];
  nursing_home_id?: string;
  cash_list_transaction_group_id?: string | null;
  receipt_type?: string;
  service_provider?: string;
  use_individual_dates?: boolean;
}

export interface CreateReceiptBatchCashTransactionGroup {
  cash_list_id: string;
  coins?: Array<{ factor: number; amount: number }>;
  bank_account_amount?: number;
  notes?: string;
}

export interface ReceiptBatchResult {
  data: ReceiptBatch;
}

export interface ReceiptBatchListResult {
  data: ReceiptBatch[];
  meta: {
    total_count: number;
    page_count: number;
    page_size: number;
    total_pages: number;
  };
}

export interface ReceiptBatchJob {
  id: string;
  status: 'pending' | 'partial' | 'successful' | 'failed';
  submission_date: Date;
}

export interface ReceiptBatchJobResult {
  data: ReceiptBatchJob;
}

export interface ReceiptBatchListParams {
  params: {
    page?: number;
    page_size?: number;
    nursing_home_ids?: string[];
    status?: Array<'pending' | 'partial' | 'successful' | 'failed'> | null;
    created_by_employee_ids?: string[];
    order_by?: Array<'updated_on' | 'created_on'>;
    order_directions?: Array<'asc' | 'desc'>;
    service_provider_name?: string;
    receipt_type_ids?: string[];
    service_provider_ids?: string[];
    recurring_item_ids?: string[];
    /** Datetimes, `[from, to)`: `to` is exclusive, e.g. the start of the day after the last included day. */
    receipt_date_range?: [Date | undefined, Date | undefined];
    /** Datetimes, `[from, to)`: `to` is exclusive, e.g. the start of the day after the last included day. */
    submission_date_range?: [Date | undefined, Date | undefined];
    total_amount_range?: [number | undefined, number | undefined];
    entry_count_range?: [number | undefined, number | undefined];
  };
}

export interface IReceiptBatchPhoenixApi extends Api {
  getReceiptBatches(
    dataIn: ReceiptBatchListParams
  ): Promise<ReceiptBatchListResult>;

  getReceiptBatch(dataIn: {
    params: { batchId: string };
  }): Promise<ReceiptBatchResult>;

  postReceiptBatchPreview(dataIn: {
    params: { batchId: string };
  }): Promise<Blob>;

  postReceiptBatch(dataIn: {
    body: Omit<UpdateReceiptBatch, 'id'>;
  }): Promise<ReceiptBatchResult>;

  patchReceiptBatch(dataIn: {
    params: { batchId: string };
    body: UpdateReceiptBatch;
  }): Promise<ReceiptBatchResult>;

  postReceiptBatchCashTransactionGroup(dataIn: {
    params: { batchId: string };
    body: CreateReceiptBatchCashTransactionGroup;
  }): Promise<ReceiptBatchResult>;

  deleteReceiptBatch(dataIn: {
    params: { batchId: string };
  }): Promise<{ status: 'OK' }>;

  postReceiptBatchEntry(dataIn: {
    params: { batchId: string };
    body: { data: UpdateReceiptBatchEntry[] };
  }): Promise<ReceiptBatchResult>;

  postReceiptBatchEntryPreview(dataIn: {
    params: { batchId: string; entryId: string };
  }): Promise<Blob>;

  patchReceiptBatchEntry(dataIn: {
    params: { batchId: string; entryId: string };
    body: UpdateReceiptBatchEntry;
  }): Promise<ReceiptBatchResult>;

  deleteReceiptBatchEntry(dataIn: {
    params: {
      batchId: string;
      entryId: string;
    };
  }): Promise<ReceiptBatchResult>;

  deleteReceiptBatchEntries(dataIn: {
    params: {
      batchId: string;
    };
    body: {
      entry_ids: string[];
    };
  }): Promise<ReceiptBatchResult>;

  submitReceiptBatch(dataIn: { params: { batchId: string } }): Promise<void>;

  submitReceiptBatchAsync(dataIn: {
    params: { batchId: string };
  }): Promise<ReceiptBatchResult>;

  getReceiptBatchJobStatus(dataIn: {
    params: { batchId: string };
  }): Promise<ReceiptBatchJobResult>;
}

export class ReceiptBatchPhoenixApi implements IReceiptBatchPhoenixApi {
  baseUrl: string;

  getReceiptBatches: IReceiptBatchPhoenixApi['getReceiptBatches'];
  getReceiptBatch: IReceiptBatchPhoenixApi['getReceiptBatch'];
  postReceiptBatch: IReceiptBatchPhoenixApi['postReceiptBatch'];
  patchReceiptBatch: IReceiptBatchPhoenixApi['patchReceiptBatch'];
  postReceiptBatchCashTransactionGroup: IReceiptBatchPhoenixApi['postReceiptBatchCashTransactionGroup'];
  postReceiptBatchEntry: IReceiptBatchPhoenixApi['postReceiptBatchEntry'];
  patchReceiptBatchEntry: IReceiptBatchPhoenixApi['patchReceiptBatchEntry'];
  deleteReceiptBatch: IReceiptBatchPhoenixApi['deleteReceiptBatch'];
  deleteReceiptBatchEntry: IReceiptBatchPhoenixApi['deleteReceiptBatchEntry'];
  deleteReceiptBatchEntries: IReceiptBatchPhoenixApi['deleteReceiptBatchEntries'];
  postReceiptBatchPreview: IReceiptBatchPhoenixApi['postReceiptBatchPreview'];
  postReceiptBatchEntryPreview: IReceiptBatchPhoenixApi['postReceiptBatchEntryPreview'];
  submitReceiptBatch: IReceiptBatchPhoenixApi['submitReceiptBatch'];
  submitReceiptBatchAsync: IReceiptBatchPhoenixApi['submitReceiptBatchAsync'];
  getReceiptBatchJobStatus: IReceiptBatchPhoenixApi['getReceiptBatchJobStatus'];

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    this.baseUrl = getPhoenixApiTenantUrl(tenantId, apiUrl);

    this.getReceiptBatches = get({
      authContext,
      url: (params) => {
        const filter = new Filter();

        if (params.status !== undefined) {
          filter.field(
            'status',
            params.status === null ? 'empty' : 'in',
            params.status === null ? 'true' : params.status
          );
        }

        // `if (x)` on these array params (no length check) is intentional:
        // an explicit `[]` still sends the (valueless) `in` filter, matching
        // zero rows, rather than being treated as absent like
        // `fieldIfPresent` would.
        if (params.nursing_home_ids) {
          filter.field('nursing_home_id', 'in', params.nursing_home_ids);
        }
        if (params.created_by_employee_ids) {
          filter.field(
            'created_by_employee_id',
            'in',
            params.created_by_employee_ids
          );
        }
        filter.fieldIfPresent(
          'service_provider_name',
          'ilike',
          params.service_provider_name
        );
        if (params.receipt_type_ids) {
          filter.field('receipt_type', 'in', params.receipt_type_ids);
        }
        if (params.service_provider_ids) {
          filter.field('service_provider', 'in', params.service_provider_ids);
        }
        if (params.recurring_item_ids) {
          filter.field('recurring_item_id', 'in', params.recurring_item_ids);
        }

        filter.fieldIfPresent(
          'receipt_date',
          '>=',
          params.receipt_date_range?.[0]?.toISOString()
        );
        filter.fieldIfPresent(
          'receipt_date',
          '<',
          params.receipt_date_range?.[1]?.toISOString()
        );
        filter.fieldIfPresent(
          'submission_date',
          '>=',
          params.submission_date_range?.[0]?.toISOString()
        );
        filter.fieldIfPresent(
          'submission_date',
          '<',
          params.submission_date_range?.[1]?.toISOString()
        );

        if (params.total_amount_range && params.total_amount_range[0]) {
          filter.field(
            'total_amount',
            '>=',
            params.total_amount_range[0].toString()
          );
        }

        if (params.total_amount_range && params.total_amount_range[1]) {
          filter.field(
            'total_amount',
            '<=',
            params.total_amount_range[1].toString()
          );
        }

        if (params.entry_count_range && params.entry_count_range[0]) {
          filter.field(
            'entry_count',
            '>=',
            params.entry_count_range[0].toString()
          );
        }

        if (params.entry_count_range && params.entry_count_range[1]) {
          filter.field(
            'entry_count',
            '<=',
            params.entry_count_range[1].toString()
          );
        }

        const queryParams = filter.build();

        if (params.page !== undefined) {
          queryParams.append(`page`, params.page.toString());
        }

        if (params.page_size) {
          queryParams.append(`page_size`, params.page_size.toString());
        }

        if (params.order_by) {
          for (const orderBy of params.order_by) {
            queryParams.append(`order_by[]`, orderBy);
          }
        }

        if (params.order_directions) {
          for (const orderDirection of params.order_directions) {
            queryParams.append(`order_directions[]`, orderDirection);
          }
        }

        return `${this.baseUrl}/receipt-batch?${queryParams.toString()}`;
      },
    });

    this.getReceiptBatch = get({
      authContext,
      url: (params) => `${this.baseUrl}/receipt-batch/${params.batchId}`,
    });

    this.postReceiptBatchPreview = async (dataIn) => {
      const finalUrl = `${this.baseUrl}/receipt-batch/${dataIn.params.batchId}/preview`;

      return await superFetch(authContext, finalUrl, {
        method: 'POST',
        headers: defaultHeaders(),
      }).then((res) => res.blob());
    };

    this.postReceiptBatchEntryPreview = async (dataIn) => {
      const finalUrl = `${this.baseUrl}/receipt-batch/${dataIn.params.batchId}/entry/${dataIn.params.entryId}/preview`;

      return await superFetch(authContext, finalUrl, {
        method: 'POST',
        headers: defaultHeaders(),
      }).then((res) => res.blob());
    };

    this.postReceiptBatch = modify({
      method: 'POST',
      authContext,
      url: `${this.baseUrl}/receipt-batch`,
    });

    this.patchReceiptBatch = modify({
      method: 'PATCH',
      authContext,
      url: (params) => `${this.baseUrl}/receipt-batch/${params.batchId}`,
    });

    this.postReceiptBatchCashTransactionGroup = modify({
      method: 'POST',
      authContext,
      transformBody: ({ coins, ...rest }) =>
        coins ? { ...rest, coins: addMissingFactorsAndSort(coins) } : rest,
      url: (params) =>
        `${this.baseUrl}/receipt-batch/${params.batchId}/cash-transaction-group`,
    });

    this.postReceiptBatchEntry = modify({
      method: 'POST',
      authContext,
      url: (params) => `${this.baseUrl}/receipt-batch/${params.batchId}/entry`,
    });

    this.patchReceiptBatchEntry = modify({
      method: 'PATCH',
      authContext,
      url: (params) =>
        `${this.baseUrl}/receipt-batch/${params.batchId}/entry/${params.entryId}`,
    });

    this.deleteReceiptBatch = modify({
      method: 'DELETE',
      authContext,
      url: (params) => `${this.baseUrl}/receipt-batch/${params.batchId}`,
    });

    this.deleteReceiptBatchEntry = modify({
      method: 'DELETE',
      authContext,
      url: (params) =>
        `${this.baseUrl}/receipt-batch/${params.batchId}/entry/${params.entryId}`,
    });

    this.deleteReceiptBatchEntries = modify({
      method: 'DELETE',
      authContext,
      url: (params) =>
        `${this.baseUrl}/receipt-batch/${params.batchId}/entries`,
    });

    this.submitReceiptBatch = modify({
      method: 'POST',
      authContext,
      url: (params) => `${this.baseUrl}/receipt-batch/${params.batchId}/submit`,
    });

    this.submitReceiptBatchAsync = modify({
      method: 'POST',
      authContext,
      url: (params) =>
        `${this.baseUrl}/receipt-batch/${params.batchId}/submit-async`,
    });

    this.getReceiptBatchJobStatus = get({
      authContext,
      url: (params) => `${this.baseUrl}/receipt-batch/${params.batchId}/job`,
    });
  }
}
